import { apiPrefix, getTenant, assetUrl } from './tenant'
import { createCardCache, memoryStorage } from './card-cache'

// katex 按需加载：只有正文里有公式的卡片才需要（整站 119 张里 3 张），常驻会让首屏 JS 多 ~75KB(gzip)
let katexLib = null
let katexLoading = null
const ensureKatex = () => {
    if (katexLib) return Promise.resolve(katexLib)
    if (!katexLoading) {
        katexLoading = Promise.all([import('katex'), import('katex/dist/katex.min.css')])
            .then(([mod]) => {
                katexLib = mod.default || mod
                return katexLib
            })
            .catch((error) => {
                katexLoading = null
                console.log('katex load failed', error)
                return null
            })
    }
    return katexLoading
}
const renderMath = (tex, node, displayMode) => {
    if (katexLib) {
        katexLib.render(tex, node, { throwOnError: false, displayMode: displayMode })
    } else {
        // 正常流程里 ensureCardContents 已预加载 katex；兜底先显示源码，不阻塞渲染
        node.textContent = tex
    }
}

// 收集节点子树的纯文本（math_inline/math_block 的 LaTeX 源码存在 text 子节点里）
const collectText = (node) => {
    let out = ''
    if (node['text']) out += node['text']
    if (node['content']) {
        for (const child of node['content']) out += collectText(child)
    }
    return out
}

// date 节点（dateChip）展示文本：固定 en-US，对齐 Heptabase「Aug 21, 2026」
const formatDateChip = (iso, withTime) => {
    if (!iso) return ''
    // date-only 字符串禁走 Date('YYYY-MM-DD')：会按 UTC 解析导致西半球时区差一天
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/)
    const d = m
        ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] || 0), Number(m[5] || 0))
        : new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return new Intl.DateTimeFormat('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        ...(withTime ? { hour: '2-digit', minute: '2-digit', hour12: false } : {}),
    }).format(d)
}

// 最近一次从后端拉取的数据（handleHeptabaseData 处理后赋值），供 getCardName 等同步逻辑使用
let LATEST_DATA = null

const getCardName = (cardId) => {

    const cards = LATEST_DATA ? LATEST_DATA.data.cards : []
    for (let i = 0; i < cards.length; i++) {
        if (cards[i]['id'] === cardId) {
            return cards[i]
        }
    }

    return null

}



// 计算指定时间与当前的时间差
const getLastEditedTime = (dateBegin) => {

    dateBegin = new Date(dateBegin)

    let dateEnd = new Date();

    // 时间差的毫秒数
    let dateDiff = dateEnd.getTime() - dateBegin.getTime()
    // 时间差的天数
    let dayDiff = Math.floor(dateDiff / (24 * 3600 * 1000))

    // 计算除天数外剩余的毫秒数
    let leave1 = dateDiff % (24 * 3600 * 1000)
    // 小时数
    let hours = Math.floor(leave1 / (3600 * 1000))

    // 计算除小时剩余的分钟数
    let leave2 = leave1 % (3600 * 1000)
    // 分钟数
    let minutes = Math.floor(leave2 / (60 * 1000))

    //计算相差的秒数
    let leave3 = leave2 % (60 * 1000)
    let seconds = Math.round(leave3 / 1000)

    return { 'day': dayDiff, 'hours': hours, 'minutes': minutes, 'seconds': seconds }

}



// 修复单个 md 文件中的 img
const getClearImag = (card) => {

    // 修改图片后缀，避免图片无法显示
    // 找到 ![]( 符号
    // 找到上述符号之后的第 1 个 jpg#/png#/gif# 符号
    // 找到上一个步骤后的第 1 个 ) 符号
    // 删除前面 2 步 index 中间的符号

    let content = card['content']

    // 支持的图片类型
    let img_type = ['.png', '.jpeg', '.jpg', '.gif']
    // 包含以下关键字则认为是图片
    let img_keyword_index = content.indexOf('![')

    while (img_keyword_index !== -1) {


        // 获取下一个 ) 索引
        let img_end_inex = content.indexOf(')', img_keyword_index)

        // 获取下一个 ] 索引
        let img_alt_end_inex = content.indexOf(']', img_keyword_index)

        // 获取图片扩展名索引
        let img_etc_index
        for (let i = 0; i < img_type.length; i++) {
            img_etc_index = content.indexOf(img_type[i], img_keyword_index + 1)
            if (img_etc_index >= 0 && img_etc_index <= img_end_inex) {

                // 如果格式字符是这种格式 ![....jpg] 内，则跳过
                if (content.substring(img_etc_index + img_type[i].length, img_etc_index + img_type[i].length + 2) === '](') {
                    img_etc_index = content.indexOf(img_type[i], img_etc_index + 1)

                }

                img_etc_index += img_type[i].length
                break;


            }
        }

        if (img_keyword_index === -1 || img_end_inex === -1 || img_etc_index === -1) {
            break
        }

        let img_alt = content.substring(img_keyword_index + 2, img_alt_end_inex)
        let img_src = content.substring(img_alt_end_inex + 2, img_etc_index)

        // console.log('image keyword');
        // console.log(img_alt);
        // console.log(img_src);

        let old_img_str = content.substring(img_keyword_index, img_end_inex + 1)


        // 获取 = 索引
        let img_width_inex = old_img_str.indexOf('=')

        if (img_width_inex > -1 && old_img_str.indexOf('{{width') < 0) {
            //将图片宽度保存到 alt 中
            img_alt = img_alt + '{{width ' + old_img_str.substring(img_width_inex + 1, old_img_str.length - 2) + '}}'
        }

        let new_img_str = '![' + img_alt + '](' + img_src + ')'

        content = content.replace(old_img_str, new_img_str)

        // 获取 ![ 索引
        img_keyword_index = content.indexOf('![', img_keyword_index + 1)


    }
    card['content'] = content
    return card

}

// 处理单个 md 文件中的超链接
const getClearCard = (card, cards) => {

    // // 找到 (./ 符号以及之后的第 1 个 ，或找到 {{ 符号 }}) 符号，截取这 2 个 index 中间的字符串
    // // 将上述字符串放在 card 数据中匹配
    // // 如果找到匹配的卡片：修改上述字符串的地址为 /post/post.id
    // let content = card['content']
    let this_card_id = card['id']

    // 处理反向连接
    let backLinks = []
    if (Array.isArray(card['backlinks'])) {
        // 后端已提供精确反链（note_links 落库），直接映射为卡片对象
        backLinks = card['backlinks']
            .map((id) => cards.find((c) => c['id'] === id))
            .filter(Boolean)
    } else {
        // 旧缓存兼容：如果 A 卡片中存在当前笔记的 ID，则 A 卡片为当前笔记的反向链接之一
        for (let i = 0; i < cards.length; i++) {
            let content = cards[i]['content']
            if (typeof (content) !== 'string') {
                content = cards[i]['content'].innerHTML
            }

            if (content.indexOf(this_card_id) >= 0 && cards[i]['id'] !== this_card_id) {

                backLinks.push(cards[i])

            }


        }
    }

    // card['content'] = content
    return { 'card': card, 'backLinks': backLinks }

}

// ============================================================
// 数据层：目录（/v1/index，不含正文，十几 KB）+ 正文按需（/v1/cards/:id/content?h=<contentHash>）
// - 目录：localStorage 缓存 + 每次带 If-None-Match 的一次请求（没变 304），替代旧的 /etag → /whiteboard 两连
// - 正文：内容寻址缓存（card-cache.js），同一哈希永不过期；渲染器 heptaToMD 不改，拿到的仍是 hepta JSON 字符串
// - 已打开的页面切回前台 / 窗口聚焦时重验目录并推送（onSiteDataChange），站点设置、发布、改名即时生效
// - 目录接口不可用（网络抖动 / 旧服务端）时退回整包 /whiteboard，行为与旧版一致
// ============================================================
const indexKey = () => 'notes_index_v1:' + getTenant()

const storage = (() => {
    try {
        const ls = window.localStorage
        ls.setItem('__notes_probe__', '1')
        ls.removeItem('__notes_probe__')
        return ls
    } catch (error) {
        return memoryStorage()
    }
})()

// 旧版整包缓存（含全部正文，1MB+；同源所有站点共用 5MB 配额）：清掉给正文缓存腾地方
try {
    if (typeof storage.length === 'number' && typeof storage.key === 'function') {
        for (let i = storage.length - 1; i >= 0; i--) {
            const k = storage.key(i)
            if (k && k.indexOf('notes_data_v2:') === 0) storage.removeItem(k)
        }
    }
} catch (error) {
    // ignore
}

const cardCache = createCardCache(storage)
// 本页已拿到的正文（contentHash → hepta JSON 字符串）：目录更新后正文没变的卡片直接复用
const memoryContents = new Map()
let LATEST_ETAG = null

const readIndexCache = () => {
    try {
        const raw = storage.getItem(indexKey())
        const v = raw ? JSON.parse(raw) : null
        return v && v.payload && v.payload.data && Array.isArray(v.payload.data.cards) ? v : null
    } catch (error) {
        return null
    }
}

const writeIndexCache = (etag, payload) => {
    try {
        storage.setItem(indexKey(), JSON.stringify({ etag: etag, payload: payload }))
    } catch (error) {
        console.log(error);
    }
}

const clearIndexCache = () => {
    try {
        storage.removeItem(indexKey())
    } catch (error) {
        // ignore
    }
}

// 站点配置（后台设置，随目录下发）；未拉取时回退缓存，都没有则 null
const getSiteConfig = () => {
    if (LATEST_DATA && LATEST_DATA.site) return LATEST_DATA.site
    const cached = readIndexCache()
    return (cached && cached.payload.site) || null
}

const httpError = (message, status) => {
    const err = new Error(message)
    err.status = status
    return err
}

// 目录请求：带上次的 ETag，没变回 304。no-store 绕开浏览器 HTTP 缓存——缓存层只认 localStorage，
// 否则 stale-while-revalidate 可能把旧内容配新号封进缓存（旧版 /whiteboard 踩过：双链改完公开页死活不刷新）
const fetchIndex = async (etag) => {
    const res = await fetch(apiPrefix() + '/v1/index', {
        cache: 'no-store',
        headers: etag ? { 'If-None-Match': etag } : {},
    })
    if (res.status === 304) return { notModified: true }
    if (res.status === 404) {
        // 公开路由中间件对「站点不存在 / 未开放」回 JSON 404；其它 404（旧服务端没有这个端点）按接口不可用处理
        const type = res.headers.get('content-type') || ''
        throw httpError(type.indexOf('json') > -1 ? 'site not found' : 'index endpoint missing', type.indexOf('json') > -1 ? 404 : 501)
    }
    if (!res.ok) throw httpError('fetch /v1/index failed: ' + res.status, res.status)
    const payload = await res.json()
    return { etag: payload.etag || res.headers.get('ETag'), payload: payload }
}

// 目录载荷 → 页面数据：补上本页已有的正文，再交给 handleHeptabaseData 排序 / 算时间差 / 找 about
const adoptIndex = (payload, etag) => {
    const cards = payload.data.cards
    for (let i = 0; i < cards.length; i++) {
        const mem = cards[i].contentHash && memoryContents.get(cards[i].contentHash)
        if (mem) cards[i].content = mem
    }
    LATEST_ETAG = etag || null
    return handleHeptabaseData(payload)
}

// 整包兜底（旧契约，卡片自带正文）
const fetchLegacyPayload = async () => {
    const res = await fetch(apiPrefix() + '/whiteboard', { cache: 'no-store' })
    if (res.status === 404) throw httpError('site not found', 404)
    if (!res.ok) throw httpError('fetch /whiteboard failed: ' + res.status, res.status)
    const data = await res.json()
    LATEST_ETAG = res.headers.get('ETag')
    return handleHeptabaseData(data)
}

let inflight = null

// 页面数据入口（Post / Nav / Footer / 列表 / Activity 共用）：本页加载过就直接给内存里的最新数据；
// 之后的变化由 revalidateSiteData 推送给 onSiteDataChange 的订阅者
const getHeptabaseData = () => {

    if (LATEST_DATA) return Promise.resolve(LATEST_DATA)
    if (inflight) return inflight

    const current = (async () => {
        const cached = readIndexCache()
        try {
            let r = await fetchIndex(cached && cached.etag)
            if (r.notModified) {
                if (cached) return adoptIndex(cached.payload, cached.etag)
                r = await fetchIndex(null)
            }
            writeIndexCache(r.etag, r.payload)
            return adoptIndex(r.payload, r.etag)
        } catch (error) {
            if (error && error.status === 404) {
                // 站点不存在 / 未开放：不能拿缓存兜底，直接报 404 让页面显示提示
                clearIndexCache()
                throw error
            }
            console.log('getHeptabaseData index error', error);
            // 断网：有缓存先用缓存（正文走各自的缓存，拿不到的卡片按不存在处理）
            if (cached) return adoptIndex(cached.payload, cached.etag)
            return fetchLegacyPayload()
        }
    })()

    inflight = current
    const done = () => {
        if (inflight === current) inflight = null
    }
    current.then(done, done)
    return current
};

// 确保这些卡片的正文已就位（card.content 为 hepta JSON 字符串）：内存 → localStorage → 网络。
// 拿不到的（刚被撤回 / 断网且没缓存）保持 content 为空，调用方按「卡片不存在」处理
const ensureCardContents = async (ids) => {
    const data = LATEST_DATA
    if (!data) return
    const wanted = Array.from(new Set((ids || []).filter(Boolean)))
    const cards = wanted
        .map((id) => data.data.cards.find((c) => c.id === id))
        .filter(Boolean)

    await Promise.all(cards.map(async (card) => {
        if (card.content != null) return
        const hash = card.contentHash
        let content = (hash && (memoryContents.get(hash) || cardCache.get(hash))) || null
        if (!content) {
            try {
                const url = apiPrefix() + '/v1/cards/' + encodeURIComponent(card.id) + '/content' + (hash ? '?h=' + hash : '')
                const res = await fetch(url)
                if (!res.ok) return
                const body = await res.json()
                if (typeof body.content !== 'string') return
                content = body.content
                if (body.contentHash) {
                    card.contentHash = body.contentHash
                    cardCache.set(body.contentHash, content)
                }
            } catch (error) {
                console.log('load card content failed', card.id, error);
                return
            }
        }
        if (card.contentHash) memoryContents.set(card.contentHash, content)
        if (card.content == null) card.content = content
    }))

    // 有公式的卡片渲染前先把 katex 拉下来（heptaToMD 是同步的）
    if (!katexLib && cards.some((c) => typeof c.content === 'string' && c.content.indexOf('"math_') > -1)) {
        await ensureKatex()
    }
}

const listeners = new Set()

/** 订阅数据变化：{ type: 'update', data } 目录有更新；{ type: 'missing' } 站点被关闭 / 删除。返回取消订阅函数 */
const onSiteDataChange = (fn) => {
    listeners.add(fn)
    return () => {
        listeners.delete(fn)
    }
}

const emitSiteDataChange = (event) => {
    listeners.forEach((fn) => {
        try {
            fn(event)
        } catch (error) {
            console.error(error)
        }
    })
}

let revalidating = null
let lastRevalidateAt = 0

// 重验目录（一次条件请求）：变了就更新内存 + 缓存并推送；3s 内的重复触发（focus 与 visibilitychange 常连发）合并
const revalidateSiteData = () => {
    if (!getTenant() || !LATEST_DATA) return Promise.resolve(false)
    if (revalidating) return revalidating
    if (Date.now() - lastRevalidateAt < 3000) return Promise.resolve(false)
    lastRevalidateAt = Date.now()
    revalidating = fetchIndex(LATEST_ETAG)
        .then((r) => {
            if (r.notModified) return false
            writeIndexCache(r.etag, r.payload)
            const data = adoptIndex(r.payload, r.etag)
            emitSiteDataChange({ type: 'update', data: data })
            return true
        })
        .catch((error) => {
            if (error && error.status === 404) {
                clearIndexCache()
                emitSiteDataChange({ type: 'missing' })
            }
            return false
        })
        .finally(() => {
            revalidating = null
        })
    return revalidating
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') revalidateSiteData()
    })
    window.addEventListener('focus', () => {
        revalidateSiteData()
    })
}


// 按站点设置注入 GA（每个用户的站各自上报；没填就不注入）
let gaInjected = ''
const injectGa = (id) => {
    if (!id || gaInjected === id || typeof document === 'undefined') return
    gaInjected = id
    const s = document.createElement('script')
    s.async = true
    s.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(id)
    document.head.appendChild(s)
    window.dataLayer = window.dataLayer || []
    window.gtag = window.gtag || function () { window.dataLayer.push(arguments) }
    window.gtag('js', new Date())
    window.gtag('config', id)
}

// 按站点设置切换 favicon / apple-touch-icon（每个用户的站各自设置；没填保持 index.html 的默认图）
let iconApplied = ''
const applySiteIcons = (site) => {
    if (typeof document === 'undefined') return
    const favicon = site.faviconUrl || site.logoUrl || ''
    if (!favicon || iconApplied === favicon) return
    iconApplied = favicon
    const href = assetUrl(favicon)
    // Chrome 只改已有 <link> 的 href 时常沿用首屏缓存的默认图：把所有 icon 类链接（含 alternate icon 兜底）
    // 整个移除再插入新节点，才会可靠地重新取图
    document.head.querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"]').forEach((l) => l.remove())
    const add = (rel) => {
        const link = document.createElement('link')
        link.rel = rel
        link.href = href
        if (/\.svg$/i.test(href)) link.type = 'image/svg+xml'
        document.head.appendChild(link)
    }
    add('icon')
    add('apple-touch-icon')
}

const handleHeptabaseData = (data) => {

    if (data && data.site) {
        injectGa(data.site.ga)
        applySiteIcons(data.site)
    }

    data.data.cards = data.data.cards.sort((a, b) => {

        // 最近编辑时间
        return b.lastEditedTime < a.lastEditedTime ? -1 : 1

    })

    let pages = {}
    // 获取 About、Projects 页面的数据
    pages.about = undefined
    pages.firstPage = undefined
    // pages.projects = undefined

    // 存储去重后的数组
    let new_cards = []
    // 存储卡片 ID，用户判断是否重复
    let cards_id = []



    for (let i = 0; i < data.data.cards.length; i++) {

        // 首页
        if (data.data.cards[i]['title'].toLowerCase() === 'about') {

            pages.about = data.data.cards[i]

        }



        // Projects
        // if (data.data.cards[i]['title'].toLowerCase() === 'projects') {

        //     pages.projects = data.data.cards[i]

        // }

        // 去重
        if (cards_id.indexOf(data.data.cards[i]['id']) > -1) {
            // 已存在此卡片，则忽略
            // console.log(data.cards[i]);
        } else {

            // 不存在此卡片

            // 最近编辑的时间差
            let timeDiff = getLastEditedTime(data.data.cards[i]['lastEditedTime'])
            data.data.cards[i].lastEditedTimeDiff = ''
            if (timeDiff['day'] > 0) {
                data.data.cards[i].lastEditedTimeDiff = 'Edited ' + timeDiff['day'] + ' days ago'
            } else if (timeDiff['hours'] > 0) {

                data.data.cards[i].lastEditedTimeDiff = 'Edited ' + timeDiff['hours'] + ' hours ago'

            } else if (timeDiff['minutes'] > 0) {

                data.data.cards[i].lastEditedTimeDiff = 'Edited ' + timeDiff['minutes'] + ' minutes ago'

            } else {

                data.data.cards[i].lastEditedTimeDiff = 'Edited just'

            }

            new_cards.push(data.data.cards[i])
            cards_id.push(data.data.cards[i]['id'])

        }

    }

    data.data.cards = new_cards
    data.frontGetTime = Date.parse(new Date()) / 1000
    data.pages = pages

    // 供 getCardName 等同步逻辑使用（原始数据缓存由 getHeptabaseData 负责）
    LATEST_DATA = data

    return data; // 返回结果

}



/**
 * @param {Object} Hpeta_card_data Hepta 卡片数据
 * @returns 返回拼接后的 DOM 元素
 */
const heptaToMD = (Hpeta_card_data) => {

    // 如果对象已经是 DOM 则直接返回
    if (Hpeta_card_data['content'] instanceof HTMLElement) {
        return Hpeta_card_data['content']
    }

    let parent_card_id = Hpeta_card_data['id']
    let box = document.createElement('div')
    box = heptaContentTomd(JSON.parse(Hpeta_card_data['content'])['content'], box, parent_card_id)
    return box


}

/**
 * 用 marks 把一个已构建好的行内节点层层包起来（逆序：内→外嵌套）。
 * text 与 card（双链）两种行内节点共用：card 是原子节点，整句高亮时它也在选区里，
 * 不包会让公开页的双链从高亮里"漏"出来。
 * 旧实现是覆盖式（最后一个 mark 独占节点），code + 背景高亮同时存在时必丢一个。
 * @param {Node} inner_node   被包的节点
 * @param {Array} marks       ProseMirror marks
 * @param {string} parent_card_id  当前卡片 ID（内链需要）
 */
const wrapWithMarks = (inner_node, marks, parent_card_id) => {
    marks.slice().reverse().forEach(mark => {

        let wrap_node = null

        switch (mark['type']) {

            // del-line
            case 'strike':
                wrap_node = document.createElement('del')
                break

            // inline-code
            case 'code':
                wrap_node = document.createElement('code')
                break

            // italic
            case 'em':
                wrap_node = document.createElement('em')
                break

            // strong
            case 'strong':
                wrap_node = document.createElement('strong')
                break

            case 'color':

                wrap_node = document.createElement('span')

                if (mark['attrs']['color']) {

                    if (mark['attrs']['type'] === 'background') {
                        wrap_node.classList.add('highlight_bg')
                    } else {
                        wrap_node.classList.add('highlight_color')
                    }

                }

                break

            case 'link':

                if (mark['attrs']['data-internal-href'] !== null) {
                    // 内部卡片链接
                    wrap_node = document.createElement('span')
                    wrap_node.classList.add('my_link')
                    wrap_node.classList.add('article_link')
                    wrap_node.setAttribute('path', '/post/' + mark['attrs']['data-internal-href'].replace('meta://card/', ''))
                    wrap_node.setAttribute('parent_note_id', parent_card_id)

                } else {

                    if (mark['attrs']['href'].indexOf('app.heptabase') > -1 && mark['attrs']['href'].indexOf('card/') > -1) {
                        // Link to block
                        // 获取 card ID
                        let card_id_index_start = mark['attrs']['href'].indexOf('card/')
                        let card_id_index_end = mark['attrs']['href'].indexOf('#')

                        if (card_id_index_start > -1) {
                            let card_id = mark['attrs']['href'].substring(card_id_index_start + 5, card_id_index_end > -1 ? card_id_index_end : mark['attrs']['href'].length)

                            wrap_node = document.createElement('span')
                            wrap_node.classList.add('my_link')
                            wrap_node.classList.add('article_link')
                            wrap_node.setAttribute('path', '/post/' + card_id)
                            wrap_node.setAttribute('parent_note_id', parent_card_id)

                        } else {
                            // 外链
                            wrap_node = document.createElement('a')
                            wrap_node.classList.add('external_link')
                            wrap_node.href = mark['attrs']['href']
                        }

                    } else {
                        // 外链
                        wrap_node = document.createElement('a')
                        wrap_node.classList.add('external_link')
                        wrap_node.href = mark['attrs']['href']
                    }

                }

                break
            default:
                break

        }

        if (wrap_node) {
            wrap_node.appendChild(inner_node)
            inner_node = wrap_node
        }

    });
    return inner_node
}

/**
 * 
 * @param {list} content_list   block 列表
 * @param {string} parent_node   要添加子元素的父级 DOM 元素
 * @param {string} parent_card_id  当前卡片的 ID
 * @returns 返回拼接后的 md 字符串                    
 */
const heptaContentTomd = (content_list, parent_node, parent_card_id) => {

    let new_node
    let number_list_index = 1

    //遍历 content list
    for (let i = 0; i < content_list.length; i++) {

        // 根据 type 进行处理
        switch (content_list[i]['type']) {

            case 'heading':

                new_node = document.createElement('H' + content_list[i]['attrs']['level'])

                break

            case 'card':
                new_node = document.createElement('span')
                let cardTitle = content_list[i]['attrs']['cardTitle']
                let cardId = content_list[i]['attrs']['cardId']
                
                // 调试信息
                console.log('处理卡片链接：', {
                    cardTitle: cardTitle,
                    cardId: cardId,
                    parentCardId: parent_card_id
                });
                
                // 尝试获取有效的卡片标题
                if (cardTitle === undefined || cardTitle === 'Invalid card') {
                    console.log('卡片标题无效，尝试根据ID查找：', cardId);
                    // 尝试根据卡片 ID 匹配标题
                    const card = getCardName(cardId)
                    if (card) {
                        cardTitle = card.title
                        console.log('找到卡片：', cardTitle);
                    } else {
                        console.log('未找到ID为', cardId, '的卡片');
                        // 如果还是找不到，使用默认标题
                        cardTitle = '未找到卡片'
                    }
                } else {
                    console.log('使用原始卡片标题：', cardTitle);
                }
                
                new_node.innerHTML = cardTitle
                
                // 如果有有效的卡片 ID，添加链接属性
                if (cardId && cardTitle !== '未找到卡片') {
                    new_node.classList.add('my_link')
                    new_node.classList.add('article_link')
                    new_node.setAttribute('path', '/post/' + cardId)
                    new_node.setAttribute('parent_note_id', parent_card_id)
                } else {
                    new_node.classList.add('unknown_card')
                }

                // 双链上的 marks（整句高亮/加粗…）与文本一样处理
                if ('marks' in content_list[i]) {
                    new_node = wrapWithMarks(new_node, content_list[i]['marks'], parent_card_id)
                }







                break

            case 'whiteboard':
                new_node = document.createTextNode(content_list[i]['attrs']['whiteboardName'])
                break

            case 'image':

                new_node = document.createElement('div')
                let imgBox = document.createElement('img')
                imgBox.setAttribute('src', content_list[i]['attrs']['src'])
                new_node.classList.add('imgBox')
                new_node.appendChild(imgBox)

                if (content_list[i]['attrs']['width'] !== null) {
                    imgBox.setAttribute('style', 'width: ' + content_list[i]['attrs']['width']);
                }


                break

            case 'paragraph':
                // 如果父元素不是 task-list-item ，则创建 P 元素
                if (parent_node) {

                    if (parent_node['className'] !== 'task-list-item') {
                        new_node = document.createElement('p')
                    } else {
                        new_node = document.createElement('span')
                        new_node.setAttribute('style', 'margin-left:4px');
                    }

                }

                break

            case 'text':
                // 普通文本
                if (content_list[i]['text'].indexOf('{HTML}') > -1) {
                    break
                }


                // 判断是否有行内样式，例如 strong、mark

                if ('marks' in content_list[i]) {

                    // 有行内样式：交给 wrapWithMarks 逆序嵌套
                    new_node = wrapWithMarks(document.createTextNode(content_list[i]['text']), content_list[i]['marks'], parent_card_id)
                } else {
                    // 无行内样式
                    // new_node = document.createElement('span')
                    // new_node.innerText = new_node.innerText + content_list[i]['text']

                    new_node = document.createTextNode(content_list[i]['text'])

                }

                break

            case 'bullet_list_item':
                // List 容器
                const bulletListBox = document.createElement('div')
                bulletListBox.classList.add('listBox')

                // List 手柄
                const bulletHand = document.createElement('div')
                bulletHand.classList.add('listBullet')

                // List 内容
                new_node = document.createElement('div')
                new_node.setAttribute('style', 'overflow: auto');

                bulletListBox.appendChild(bulletHand)
                bulletListBox.appendChild(new_node)

                parent_node.appendChild(bulletListBox)

                break

            case 'numbered_list_item':

                // 如果上一个节点不是 number_list 则此节点的 index 为 1，否则 index +=1
                if (i > 0) {
                    if (content_list[i - 1]['type'] !== 'numbered_list_item') {
                        number_list_index = 1
                    } else {
                        number_list_index += 1
                    }
                }

                // List 容器
                const numberListBox = document.createElement('div')
                numberListBox.classList.add('listBox')

                // List 手柄
                const numberHand = document.createElement('div')
                // numberHand.classList.add('listBullet')
                numberHand.classList.add('numberListBullet')
                numberHand.setAttribute('data-before', number_list_index + '.')
                // numberHand.attr('--before-content', beforeContent)

                // List 内容
                new_node = document.createElement('div')
                new_node.setAttribute('style', 'overflow: auto');

                numberListBox.appendChild(numberHand)
                numberListBox.appendChild(new_node)

                parent_node.appendChild(numberListBox)

                break

            case 'todo_list_item':
                new_node = document.createElement('li')

                let task_input = document.createElement('input')
                task_input.type = 'checkbox'
                // task_input.checked = 'true'
                if (content_list[i]['attrs']['checked']) {
                    task_input.setAttribute("checked", content_list[i]['attrs']['checked']);
                }

                task_input.disabled = true

                new_node.classList.add('task-list-item')
                // new_node.setAttribute('style', 'margin: 16px 0');
                new_node.appendChild(task_input)
                break

            case 'ordered_list':
                new_node = document.createElement('ol')
                break

            case 'bullet_list':
                new_node = document.createElement('ul')
                break

            case 'toggle_list':
                new_node = document.createElement('div')
                break

            case 'toggle_list_item':
                new_node = document.createElement('div')
                break

            case 'task_list':
                new_node = document.createElement('ul')
                new_node.classList.add('task-list')
                break

            case 'list_item':
                new_node = document.createElement('li')

                // 如果是 task
                if (parent_node.className.indexOf('task-list') > -1) {
                    let task_input = document.createElement('input')
                    task_input.type = 'checkbox'
                    // task_input.checked = 'true'
                    if (content_list[i]['attrs']['checked']) {
                        task_input.setAttribute("checked", content_list[i]['attrs']['checked']);
                    }

                    task_input.disabled = true

                    new_node.classList.add('task-list-item')
                    // new_node.setAttribute('style', 'margin: 16px 0');
                    new_node.appendChild(task_input)
                }
                break

            case 'horizontal_rule':
                new_node = document.createElement('hr')
                break

            case 'blockquote':
                new_node = document.createElement('blockquote')
                break

            case 'code_block':

                new_node = document.createElement('pre')
                new_node.classList.add('hljs')
                new_node.classList.add('language-' + content_list[i]['attrs']['params'])

                // new_node = React.createElement('SyntaxHighlighter')

                // 直接渲染 code block 内的 HTML
                if ('content' in content_list[i] && content_list[i]['attrs']['params'] === 'html') {
                    if (content_list[i]['content'][0]['text'].indexOf('{HTML}') > -1) {
                        new_node = document.createElement('div')
                        new_node.classList.add('htmlBox')
                        new_node.innerHTML = content_list[i]['content'][0]['text'].replace('{HTML}', '')
                        // new_node.innerHTML = '<iframe style="border: 0; width: 100%; height: 120px;" src="https://bandcamp.com/EmbeddedPlayer/album=2906945127/size=large/bgcol=ffffff/linkcol=0687f5/tracklist=false/artwork=small/transparent=true/" seamless><a href="https://rhodadakar.bandcamp.com/album/as-tears-go-by">As Tears Go By by Rhoda Dakar</a></iframe>'
                    }
                }

                break

            case 'table':
                new_node = document.createElement('table')
                break

            case 'table_row':
                new_node = document.createElement('tr')
                break

            case 'table_header':
                new_node = document.createElement('th')
                break

            case 'table_cell':
                new_node = document.createElement('td')
                break

            case 'video':
                new_node = document.createElement('video')
                new_node.src = content_list[i]['attrs']['url']
                break

            case 'math_inline': {
                new_node = document.createElement('span')
                new_node.className = 'math-inline'
                renderMath(collectText(content_list[i]), new_node, false)
                delete content_list[i]['content'] // 源码已渲染，阻止递归重复输出
                break
            }

            case 'math_block': {
                new_node = document.createElement('div')
                new_node.className = 'math-block'
                renderMath(collectText(content_list[i]), new_node, true)
                delete content_list[i]['content']
                break
            }

            case 'date': {
                new_node = document.createElement('span')
                new_node.className = 'date-chip'
                const attrs = content_list[i]['attrs'] || {}
                new_node.textContent = formatDateChip(attrs['date'], attrs['withTime'])
                break
            }

            default:
                break

        }



        if (new_node !== undefined && parent_node !== undefined) {

            try {
                if (content_list[i]['type'] === 'numbered_list_item' || content_list[i]['type'] === 'bullet_list_item') {
                    // parent_node.appendChild(new_node)
                } else {
                    parent_node.appendChild(new_node)
                }


            } catch (error) {
                console.log(parent_node);
            }

        } else {
            console.log(parent_node);
        }

        if (new_node === undefined) {
            console.log(new_node);
            // new_node = parent_node
        }

        if (parent_node === undefined) {
            console.log(parent_node);
            // new_node = parent_node
        }


        // 如果还有子 content
        if ('content' in content_list[i]) {

            heptaContentTomd(content_list[i]['content'], new_node, parent_card_id)

        }

    }



    return parent_node

}


export { getHeptabaseData, getClearImag, getClearCard, heptaToMD, getSiteConfig, ensureCardContents, onSiteDataChange, revalidateSiteData }